import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

// Admin/sales-team page for two related request/approval flows:
// 1. RnR->Credit requests (jc/wizard_plan.md section 2) -- an existing
//    member requests Credit Points from a sales-team contact.
// 2. Package funding requests (jc/wizard_plan.md section 5 item 7) -- a
//    brand-new signup (created via the public /signup wizard, sponsor
//    detected as sales-team) requests that sponsor fund+activate a
//    package for them instead of paying with a card.
// Admin sees ALL requests of both kinds (plus the RnR "submit as a
// member" testing form); a sales-team member sees only requests
// addressed to them, with real approve/reject/edit-package.

function memberLabel(m) {
  if (!m) return '—';
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return `${name || '—'} (${m.affiliate_code || '—'})`;
}

function packageLabel(p) {
  if (!p) return '—';
  return `${p.name_en || p.code} (${Number(p.price).toFixed(0)})`;
}

export default function RnrCreditRequests() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const tt = (en, zh) => (isZh ? zh : en);

  const isAdmin = user && (user.role === 'admin' || user.role === 'superadmin');
  const isSalesTeam = user && user.role === 'sales-team';
  const canView = isAdmin || isSalesTeam;

  const [requestType, setRequestType] = useState('rnr_credit'); // 'rnr_credit' | 'package_funding'
  const [activeTab, setActiveTab] = useState('pending');

  const [requests, setRequests] = useState([]);
  const [packageOptions, setPackageOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [editingPackageId, setEditingPackageId] = useState(null);
  const [editPackageSelection, setEditPackageSelection] = useState('');

  const [salesTeamWellisId, setSalesTeamWellisId] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!canView) {
      navigate('/dashboard');
    }
  }, [authLoading, user, canView, navigate]);

  const endpointBase = requestType === 'rnr_credit' ? 'rnr-credit-requests' : 'package-funding-requests';

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/${endpointBase}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load requests');
      setRequests(data.requests || []);
      setPackageOptions(data.packages || []);
    } catch (err) {
      setError(err.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [token, endpointBase]);

  useEffect(() => {
    if (!user || !canView) return;
    fetchRequests();
  }, [user, canView, fetchRequests]);

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/rnr-credit-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sales_team_wellis_id: salesTeamWellisId.trim(), amount: parseFloat(amount), notes: notes.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || data.error || 'Failed to submit request');
      setStatusMessage(tt('Request submitted.', '请求已提交。'));
      setSalesTeamWellisId('');
      setAmount('');
      setNotes('');
      await fetchRequests();
    } catch (err) {
      setError(err.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (requestId) => {
    const notesInput = window.prompt(tt('Approval notes (optional):', '批准备注（可选）：'), '');
    if (notesInput === null) return;
    setProcessingId(requestId);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/${endpointBase}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ request_id: requestId, notes: notesInput || null }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to approve');
      setStatusMessage(data.message);
      await fetchRequests();
    } catch (err) {
      setError(err.message || 'Failed to approve request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId) => {
    const notesInput = window.prompt(tt('Reason for rejection:', '拒绝原因：'), '');
    if (notesInput === null) return;
    setProcessingId(requestId);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/${endpointBase}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ request_id: requestId, notes: notesInput || null }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to reject');
      setStatusMessage(data.message);
      await fetchRequests();
    } catch (err) {
      setError(err.message || 'Failed to reject request');
    } finally {
      setProcessingId(null);
    }
  };

  const startEditPackage = (r) => {
    setEditingPackageId(r.id);
    setEditPackageSelection(r.package?.id || '');
  };

  const handleSaveEditPackage = async (requestId) => {
    if (!editPackageSelection) return;
    setProcessingId(requestId);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/package-funding-requests/edit-package`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ request_id: requestId, package_id: editPackageSelection }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to update package');
      setStatusMessage(data.message);
      setEditingPackageId(null);
      await fetchRequests();
    } catch (err) {
      setError(err.message || 'Failed to update package');
    } finally {
      setProcessingId(null);
    }
  };

  if (!user || !canView) return null;

  const cardStyle = { backgroundColor: '#0d2818', borderColor: '#c9a961' };
  const inputStyle = { backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' };
  const filtered = requests.filter((r) => r.status === activeTab);
  const counts = {
    pending: requests.filter((r) => r.status === 'pending').length,
    approved: requests.filter((r) => r.status === 'approved').length,
    rejected: requests.filter((r) => r.status === 'rejected').length,
  };

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-headline-lg font-bold mb-1" style={{ color: '#c9a961' }}>
          {tt('Requests', '请求')}
        </h1>
        <p className="text-body-sm mb-6" style={{ color: '#999999' }}>
          {tt(
            'Review and approve requests addressed to you from members and new signups.',
            '审核并批准会员及新注册用户向您发出的请求。'
          )}
        </p>

        <div className="grid grid-cols-2 gap-2 mb-6">
          {[
            ['rnr_credit', tt('RnR → Credit Requests', 'RnR → 认购积分请求')],
            ['package_funding', tt('Package Funding Requests', '套餐资助请求')],
          ].map(([val, label]) => (
            <button
              key={val}
              type="button"
              onClick={() => { setRequestType(val); setActiveTab('pending'); setStatusMessage(''); setError(''); }}
              className="px-3 py-2 rounded-md border text-body-sm font-medium transition"
              style={requestType === val
                ? { backgroundColor: '#c9a961', color: '#0d2818', borderColor: '#c9a961' }
                : { backgroundColor: 'transparent', color: '#b8a68f', borderColor: '#3d5a4a' }}
            >
              {label}
            </button>
          ))}
        </div>

        {requestType === 'package_funding' && (
          <p className="text-body-sm mb-6" style={{ color: '#999999' }}>
            {tt(
              'New signups whose sponsor is a sales-team member can request that you fund and activate their package instead of paying by card.',
              '若新注册用户的推荐人是销售团队成员，他们可以请求您资助并激活其套餐，而无需银行卡付款。'
            )}
          </p>
        )}

        {error && (
          <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
            {error}
          </div>
        )}
        {statusMessage && (
          <div className="border rounded-lg p-4 mb-4" style={cardStyle}>
            <span style={{ color: '#b8a68f' }}>{statusMessage}</span>
          </div>
        )}

        {isAdmin && requestType === 'rnr_credit' && (
        <form onSubmit={handleSubmitRequest} className="border rounded-lg p-6 mb-8" style={cardStyle}>
          <h3 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>
            {tt('Submit a Request (simulating a member)', '提交请求（模拟会员操作）')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <input
              type="text"
              placeholder={tt('Sales-team Wellis ID', '销售团队 Wellis ID')}
              value={salesTeamWellisId}
              onChange={(e) => setSalesTeamWellisId(e.target.value)}
              className="p-3 rounded border text-body-sm"
              style={inputStyle}
              required
            />
            <input
              type="number"
              step="0.01"
              min="0.01"
              placeholder={tt('Amount', '金额')}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="p-3 rounded border text-body-sm"
              style={inputStyle}
              required
            />
            <input
              type="text"
              placeholder={tt('Notes (optional)', '备注（可选）')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="p-3 rounded border text-body-sm"
              style={inputStyle}
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg font-medium disabled:opacity-50 text-black"
            style={{ backgroundColor: '#c9a961' }}
          >
            {submitting ? tt('Submitting...', '提交中...') : tt('Submit Request', '提交请求')}
          </button>
        </form>
        )}

        <div className="flex gap-2 mb-4 border-b flex-wrap" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
          {[
            ['pending', tt('Pending', '待处理')],
            ['approved', tt('Approved', '已批准')],
            ['rejected', tt('Rejected', '已拒绝')],
          ].map(([val, label]) => (
            <button
              key={val}
              type="button"
              onClick={() => setActiveTab(val)}
              className="px-4 py-2 text-body-sm font-medium rounded-t-lg transition-colors"
              style={activeTab === val ? { color: '#0d2818', backgroundColor: '#c9a961' } : { color: '#c9a961', backgroundColor: 'transparent' }}
            >
              {label} ({counts[val]})
            </button>
          ))}
        </div>

        {loading ? (
          <p style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
        ) : filtered.length === 0 ? (
          <div className="border rounded-lg p-6 text-center" style={cardStyle}>
            <p style={{ color: '#b8a68f' }}>{tt('No requests here.', '暂无请求。')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
              <thead>
                <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                  <th className="p-2">{requestType === 'rnr_credit' ? tt('Requester', '请求人') : tt('New Member', '新会员')}</th>
                  <th className="p-2">{tt('Sales-Team Member', '销售团队成员')}</th>
                  <th className="p-2">{requestType === 'rnr_credit' ? tt('Amount', '金额') : tt('Package', '套餐')}</th>
                  <th className="p-2">{tt('Date', '日期')}</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                    <td className="p-2" style={{ color: '#c9a961' }}>
                      {requestType === 'rnr_credit' ? memberLabel(r.requester) : memberLabel(r.new_member)}
                    </td>
                    <td className="p-2">{memberLabel(r.sales_team_member)}</td>
                    <td className="p-2">
                      {requestType === 'rnr_credit' ? (
                        Number(r.amount).toFixed(2)
                      ) : editingPackageId === r.id ? (
                        <div className="flex gap-1 items-center">
                          <select
                            value={editPackageSelection}
                            onChange={(e) => setEditPackageSelection(e.target.value)}
                            className="p-1 rounded border text-body-xs"
                            style={inputStyle}
                          >
                            {packageOptions.map((p) => (
                              <option key={p.id} value={p.id}>{packageLabel(p)}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => handleSaveEditPackage(r.id)}
                            disabled={processingId === r.id}
                            className="px-2 py-1 rounded font-medium text-body-xs disabled:opacity-50"
                            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                          >
                            {tt('Save', '保存')}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingPackageId(null)}
                            className="px-2 py-1 rounded font-medium text-body-xs border"
                            style={{ backgroundColor: 'transparent', color: '#b8a68f', borderColor: '#3d5a4a' }}
                          >
                            {tt('Cancel', '取消')}
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2 items-center">
                          <span>{packageLabel(r.package)}</span>
                          {r.status === 'pending' && (
                            <button
                              type="button"
                              onClick={() => startEditPackage(r)}
                              className="text-body-xs underline"
                              style={{ color: '#c9a961' }}
                            >
                              {tt('Edit', '编辑')}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="p-2">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="p-2">
                      {r.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleApprove(r.id)}
                            disabled={processingId === r.id}
                            className="px-3 py-1 rounded font-medium text-body-xs disabled:opacity-50"
                            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                          >
                            {tt('Approve', '批准')}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReject(r.id)}
                            disabled={processingId === r.id}
                            className="px-3 py-1 rounded font-medium text-body-xs border disabled:opacity-50"
                            style={{ backgroundColor: 'transparent', color: '#c94961', borderColor: '#c94961' }}
                          >
                            {tt('Reject', '拒绝')}
                          </button>
                        </div>
                      )}
                      {r.status !== 'pending' && r.review_notes && (
                        <span className="text-body-xs" style={{ color: '#999999' }}>{r.review_notes}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
